import UploadCloudIcon from 'ext-ui/svg/icon/UploadCloudIcon'
import React, { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import * as style from 'src/components/CsvDropzone/CsvDropzone.style'

interface CsvDropzoneProps {
  /** Called with the chosen file — wired to the context's `acceptFile`. */
  onFile: (file: File) => void
  /** Accessible name for the hidden file input. */
  inputAriaLabel: string
  /** Text after the inline "Click to upload" affordance. */
  promptText: string
  /** Inline, underlined "Click to upload" affordance text. */
  promptLinkText: string
  /** Single-line hint under the prompt (e.g. "CSV only · max 10 MB"). */
  hint: string
  maxSize?: number
  testId?: string
}

/**
 * Single-file CSV dropzone for Bulk Invite. Reuses the receipt-capture
 * (`WideDropzone`) drag/drop affordance and visual pattern — a dashed drop
 * target with a "Click to upload or drag and drop" prompt — implemented fresh
 * here so the shared receipt component is left untouched.
 *
 * The drop target is clickable and keyboard-operable: react-dropzone forwards
 * click/Enter/Space to the hidden (but focusable) file input via the root
 * props, and the visible focus ring is lifted onto the shell with
 * `:focus-within` in the style file.
 */
const CsvDropzone: React.FC<CsvDropzoneProps> = ({
  onFile,
  inputAriaLabel,
  promptText,
  promptLinkText,
  hint,
  maxSize,
  testId = 'bulkInviteDropzone',
}) => {
  const onDrop = useCallback(
    (files: File[]): void => {
      if (files[0]) onFile(files[0])
    },
    [onFile],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    onDrop,
    accept: {
      'text/csv': ['.csv', '.txt'],
      'text/plain': ['.txt'],
    },
    maxSize,
  })

  return (
    <div
      {...getRootProps()}
      data-testid={testId}
      css={style.dropzone(isDragActive)}
    >
      <input
        {...getInputProps()}
        css={style.visuallyHiddenInput}
        aria-label={inputAriaLabel}
      />
      <span aria-hidden="true" css={style.icon}>
        <UploadCloudIcon width={24} height={24} />
      </span>
      <span css={style.primary}>
        <span css={style.link}>{promptLinkText}</span> {promptText}
      </span>
      <span css={style.hint}>{hint}</span>
    </div>
  )
}

export default CsvDropzone
